/**
 * Design-theme panel — the working surface for the video's art direction.
 *
 * A PANEL, not a modal (unlike the translate dialog): this is long-form editing
 * of eight fields plus a palette, done while looking at the board and the
 * script. A modal would have to be dismissed to see its own output.
 *
 * Field edits commit on BLUR, not per keystroke. The stored fields are plain
 * LWW strings (C2 gives each field its own key, which stops two people editing
 * two DIFFERENT fields from clobbering each other — it does not help two people
 * in the SAME field). Committing on blur shrinks that window from every
 * keystroke to one write per edit session, which is the right trade for a
 * surface two people rarely touch simultaneously.
 */
import { type ArtDirectionTextField, type PaletteSwatch } from "@kola/shared";
import { useEffect, useState } from "react";
import { useStore } from "zustand";

import {
  ApiError,
  assetUrl,
  cancelDesignImages,
  dismissDesignImagesState,
  generateArtDirection,
  startDesignImages,
  uploadAsset,
} from "../../core/api.js";
import type { BoardSession } from "../../core/session.js";
import { iconCopy, iconDownload } from "../../ui/icons.js";
import { PanelResizeHandle } from "../../ui/PanelResizeHandle.js";
import { ProgressBar } from "../../ui/ProgressBar.js";
import { useDockedPanelWidth } from "../../ui/useDockedPanelWidth.js";
import { showToast } from "../../ui/toast.js";
import { optimizeForUpload } from "../elements/optimizeImage.js";
import { copyDesignDescription } from "./description.js";
import { downloadDesignRef, downloadDesignShot } from "./download.js";
import { FIELD_COPY, FIELD_ORDER } from "./fields.js";
import { ReportErrorButton } from "../debug/ReportErrorButton.js";
import type { DesignUiStore } from "./store.js";
import { readScriptSample, useDesignTheme } from "./use-design.js";

export interface DesignPanelProps {
  session: BoardSession;
  ui: DesignUiStore;
}

/**
 * What one run renders, for the hint. NOT sent to the server — the page list is
 * the server's fixed catalogue (`design-theme/elements.ts`) and the run reports
 * its real `total` back — so this is copy, and a mismatch costs a wrong sentence
 * rather than a wrong run.
 */
const SHEET_DESCRIPTION =
  "text (boxed and unboxed, with partial highlights and emphasis styles), bar / pie / highlighted charts, animation parts, and world and city maps";

/**
 * A 422 from the art-direction route, in the user's terms. The server sends
 * WHICH model produced the unusable reply and HOW it was unusable, and those
 * two facts point at different fixes — one flat "try again" sent people to
 * re-roll a model that was never going to answer (F-106's rule, applied to the
 * one failure that survives a tolerant parse).
 */
function describeUnusable(error: ApiError): string {
  const body = error.body as { reason?: string; model?: string } | undefined;
  const model = body?.model ?? "The model";
  if (body?.reason === "truncated") {
    return `${model} ran out of room before finishing the art direction, even after an automatic retry. Your existing art direction is unchanged. Try a shorter brief or a different Art Direction model.`;
  }
  if (body?.reason === "no-text") {
    return `${model} replied with no text at all. That is usually a reasoning model that spent its budget thinking, or an image model set on a text function — an admin can pick a different model for Art Direction in Admin.`;
  }
  return `${model} didn't return an art direction we could read. Try again — if it keeps happening, an admin can pick a different model in Admin.`;
}

/** Why a generation could not start, in the user's terms. */
function describeError(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return error instanceof Error ? error.message : "Something went wrong.";
  }
  switch (error.code) {
    case "design-not-configured":
      return "AI is not configured on this server yet (no OpenRouter key).";
    case "unusable-response":
      return describeUnusable(error);
    case "upstream-error":
      return "The AI provider failed. Try again in a moment.";
    case "rate-limited":
    case "too-many-concurrent":
      return "You've generated a lot recently — give it a minute.";
    case "server-draining":
      return "The server is restarting. Try again in a few seconds.";
    case "too-many-lines":
    case "too-many-chars":
      return "This script is too long to sample. Report this — it shouldn't happen.";
    case "image-generation-unsupported":
      return "This server's AI model can't generate images. An admin can pick an image model in Settings.";
    case "no-art-direction":
      return "Write the art direction first — frames are generated from it.";
    case "already-running":
      return "A generation is already running on this board.";
    case "too-many-images":
      return "That's more frames than this server allows in one run.";
    default:
      return error.message;
  }
}

export function DesignPanel({ session, ui }: DesignPanelProps) {
  const panelWidth = useDockedPanelWidth("design", 320);
  const theme = useDesignTheme(session);
  const generating = useStore(ui, (s) => s.generating);
  const confirmReplace = useStore(ui, (s) => s.confirmReplace);
  const error = useStore(ui, (s) => s.error);

  async function runGeneration() {
    ui.getState().setConfirmReplace(false);
    ui.getState().setError(null);

    const { sample, totalLines } = readScriptSample(session);
    if (sample.length === 0) {
      ui.getState().setError(
        totalLines === 0
          ? "There's no script yet — write or import one into the Original Script column first."
          : "Couldn't read the script column.",
      );
      return;
    }

    ui.getState().setGenerating(true);
    try {
      const result = await generateArtDirection(
        session.boardId,
        sample,
        session.board.getDesignBrief(),
      );
      if (!session.applyArtDirection(result.artDirection, result.model)) {
        ui.getState().setError("You're offline — reconnect before generating.");
        return;
      }
      showToast(`Art direction written from ${totalLines} script lines.`);
    } catch (err) {
      ui.getState().setError(describeError(err));
    } finally {
      ui.getState().setGenerating(false);
    }
  }

  function onGenerateClick() {
    // Regenerating silently over someone's hand-edits is the one destructive
    // thing this panel can do — a generation is ONE undo step, but only for
    // the person who pressed it.
    if (theme.present) ui.getState().setConfirmReplace(true);
    else void runGeneration();
  }

  return (
    <aside className="design-panel" aria-label="Design theme" style={{ width: panelWidth.width }}>
      <PanelResizeHandle
        label="Resize the design panel"
        width={panelWidth.width}
        bounds={panelWidth.bounds}
        onResize={panelWidth.setWidth}
        onCommit={panelWidth.commit}
        onReset={panelWidth.reset}
      />
      <div className="design-panel-head">
        <span className="design-panel-title">Design theme</span>
        <span className="spacer" />
        {/* F-136: the whole art direction as text — the panel's eight boxes
            cannot be selected in one sweep, and a textarea's selection stops
            at its own box, so the palette was uncopyable by hand. */}
        <button
          type="button"
          className="icon-btn"
          onClick={() => void copyDesignDescription(theme.artDirection)}
          aria-label="Copy design description"
          data-tip="Copy description"
        >
          {iconCopy}
        </button>
        <button
          type="button"
          className="icon-btn"
          onClick={() => ui.getState().setPanelOpen(false)}
          aria-label="Close design panel"
          data-tip="Close"
        >
          ✕
        </button>
      </div>

      <div className="design-panel-body">
        <section className="design-section">
          <label className="design-label" htmlFor="design-brief">
            Your brief
          </label>
          <p className="design-hint">
            Standing instructions for the generator. Yours — it never rewrites this.
          </p>
          <CommitTextarea
            id="design-brief"
            rows={3}
            value={theme.brief}
            placeholder="Colder than usual. 'Kola' is a product name — leave it in English."
            onCommit={(next) => session.setDesignBrief(next)}
          />
        </section>

        <section className="design-section">
          <button
            type="button"
            className="btn btn-primary design-generate"
            onClick={onGenerateClick}
            disabled={generating}
          >
            {generating
              ? "Writing art direction…"
              : theme.present
                ? "Regenerate art direction"
                : "Generate art direction"}
          </button>
          {confirmReplace && (
            <div className="design-confirm" role="alertdialog" aria-label="Replace art direction">
              <p>Replace the current art direction? Your edits will be overwritten.</p>
              <div className="design-confirm-actions">
                <button type="button" className="btn" onClick={() => ui.getState().setConfirmReplace(false)}>
                  Keep it
                </button>
                <button type="button" className="btn btn-primary" onClick={() => void runGeneration()}>
                  Replace
                </button>
              </div>
            </div>
          )}
          {error !== null && (
            <div>
              <p className="design-error" role="alert">{error}</p>
              <ReportErrorButton key={error} source="Art direction" message={error} />
            </div>
          )}
          {theme.model !== undefined && !generating && (
            <p className="design-provenance">
              Written by <code>{theme.model}</code>
              {theme.updatedAt !== undefined && ` · edited ${formatWhen(theme.updatedAt)}`}
            </p>
          )}
        </section>

        <section className="design-section">
          <span className="design-label">Palette</span>
          <PaletteEditor
            swatches={theme.artDirection.palette}
            onChange={(next) => session.setDesignPalette(next)}
          />
        </section>

        {FIELD_ORDER.map((field) => (
          <FieldEditor
            key={field}
            field={field}
            value={theme.artDirection[field]}
            onCommit={(next) => session.setArtDirectionField(field, next)}
          />
        ))}

        {/* Last, and in this order: both of these consume the art direction
            above, so putting them before it invited "Generate key visuals"
            before the user had read a single field it generates from. */}
        <ReferenceFrames session={session} ui={ui} />
        <KeyVisuals session={session} ui={ui} />
      </div>
    </aside>
  );
}

/**
 * Style-reference frames — the thing that actually locks the house style.
 *
 * The written constant in the generator's prompt is inferred from three
 * adjectives; these are frames from real videos, and they ride along as
 * `input_references` on every image call. Worth saying so in the UI, because
 * an empty dropzone otherwise reads as optional decoration.
 */
function ReferenceFrames({ session, ui }: DesignPanelProps) {
  const theme = useDesignTheme(session);
  const [uploading, setUploading] = useState(false);

  async function addFiles(files: readonly File[]) {
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        // Same upload path as a board image drop — cap the long edge and
        // re-encode before it crosses the wire (F-30's optimizeForUpload).
        const asset = await uploadAsset(session.boardId, await optimizeForUpload(file));
        session.addDesignRef(asset.assetId, asset.w, asset.h);
      }
    } catch (err) {
      ui.getState().setImageError(describeError(err));
    } finally {
      setUploading(false);
    }
  }

  return (
    <section className="design-section">
      <span className="design-label">Background & style references</span>
      <p className="design-hint">
        Upload your background image or design examples to guide every generated image.
        Without one, AI designs a background matching your theme. Grids are optional.
      </p>

      {theme.refs.length > 0 && (
        <div className="design-refs">
          {theme.refs.map((ref, index) => (
            <figure className="design-ref" key={ref.id}>
              <img src={assetUrl(ref.assetId)} alt="Style reference" />
              {/* Saving is offered beside removing, on the opposite corner:
                  a reference someone uploaded months ago may be the only copy
                  left of that frame (F-136). */}
              <button
                type="button"
                className="design-thumb-btn design-thumb-download"
                onClick={() => void downloadDesignRef(ref, index + 1)}
                aria-label={`Download reference ${index + 1}`}
                data-tip="Download"
              >
                {iconDownload}
              </button>
              <button
                type="button"
                className="design-thumb-btn design-thumb-remove"
                onClick={() => session.deleteDesignRef(ref.id)}
                aria-label="Remove reference"
                data-tip="Remove"
                data-tip-pos="left"
              >
                ✕
              </button>
            </figure>
          ))}
        </div>
      )}

      <label className="btn design-add-ref">
        {uploading ? "Uploading…" : "+ Add background or references"}
        <input
          type="file"
          accept="image/*"
          multiple
          hidden
          disabled={uploading}
          onChange={(e) => {
            const files = [...(e.target.files ?? [])];
            e.target.value = ""; // let the same file be picked again
            void addFiles(files);
          }}
        />
      </label>
    </section>
  );
}

/**
 * Key-visual generation: the run control, the live bar, and the gallery.
 *
 * Progress is read from the DOC, not from this component's own state — the run
 * is server-side and shared, so a second person watching the same board sees
 * the same bar, and a reload mid-run still shows it.
 */
function KeyVisuals({ session, ui }: DesignPanelProps) {
  const theme = useDesignTheme(session);
  const starting = useStore(ui, (s) => s.startingImages);
  const imageError = useStore(ui, (s) => s.imageError);
  const job = theme.job;
  const running = job?.status === "running";

  async function start() {
    ui.getState().setImageError(null);
    ui.getState().setStartingImages(true);
    try {
      await startDesignImages(session.boardId);
    } catch (err) {
      ui.getState().setImageError(describeError(err));
    } finally {
      ui.getState().setStartingImages(false);
    }
  }

  async function cancel() {
    if (job === undefined) return;
    try {
      await cancelDesignImages(session.boardId, job.jobId);
    } catch (err) {
      ui.getState().setImageError(describeError(err));
    }
  }

  async function dismiss() {
    try {
      await dismissDesignImagesState(session.boardId);
    } catch {
      // 409 = the run is still going and will republish; nothing to say.
    }
  }

  return (
    <section className="design-section">
      <span className="design-label">Style sheet</span>
      <p className="design-hint">
        {theme.present
          ? `Renders one page per element — ${SHEET_DESCRIPTION} — from the art direction above, as it stands now.`
          : "Write the art direction first — the pages are generated from it."}
      </p>

      {running && job !== undefined ? (
        <>
          <ProgressBar
            percent={job.total === 0 ? 0 : Math.round((job.done / job.total) * 100)}
            label={`Generating the style sheet — ${job.done} of ${job.total}`}
          />
          <p className="design-hint">
            {job.done} of {job.total} pages
            {job.failed > 0 ? ` · ${job.failed} failed` : ""}
          </p>
          <button type="button" className="btn" onClick={() => void cancel()}>
            Stop
          </button>
        </>
      ) : (
        <button
          type="button"
          className="btn btn-primary design-generate"
          onClick={() => void start()}
          disabled={starting || !theme.present}
        >
          {starting
            ? "Starting…"
            : theme.shots.length > 0
              ? "Generate the sheet again"
              : "Generate style sheet"}
        </button>
      )}

      {imageError !== null && (
        <div>
          <p className="design-error" role="alert">{imageError}</p>
          <ReportErrorButton key={imageError} source="Style sheet" message={imageError} />
        </div>
      )}

      {job !== undefined && !running && (
        <p className="design-provenance">
          {job.status === "cancelled" ? "Stopped" : job.status === "error" ? "Failed" : "Done"} —{" "}
          {job.done - job.failed} of {job.total} pages
          {job.failed > 0 ? `, ${job.failed} failed` : ""}.{" "}
          <button type="button" className="design-band-edit" onClick={() => void dismiss()}>
            Dismiss
          </button>
        </p>
      )}

      {job !== undefined && !running && (job.status === "error" || job.failed > 0) && (
        <ReportErrorButton
          key={job.jobId}
          source="Style sheet generation"
          message={`${job.failed} of ${job.total} pages failed (status: ${job.status}).`}
          detail={`jobId=${job.jobId}`}
        />
      )}

      {theme.shots.length > 0 && (
        <div className="design-gallery">
          {/* Numbered BEFORE the reverse, so a page's download name comes from
              its place in the gallery's storage order and does not shift under
              it when the next run appends more pages. */}
          {theme.shots
            .map((shot, index) => ({ shot, position: index + 1 }))
            .reverse()
            .map(({ shot, position }) => (
              // Captioned, because a run is nine pages and "which one is the pie
              // chart" is unanswerable from a 2-up thumbnail. Shots from before
              // the catalogue existed carry no label and fall back to the
              // generic name rather than showing an empty caption.
              <figure key={shot.id} className="design-gallery-item">
                <div className="design-gallery-frame">
                  <img
                    className="design-gallery-shot"
                    src={assetUrl(shot.assetId)}
                    alt={shot.label ?? "Generated key visual"}
                    title={shot.prompt}
                  />
                  {/* F-136: a generated page is not an element until someone
                      places it, so the F-135 element download cannot reach it
                      — without this button the only way out of Kola was a
                      screenshot of a 2-up thumbnail. */}
                  <button
                    type="button"
                    className="design-thumb-btn design-thumb-download"
                    onClick={() => void downloadDesignShot(shot, position)}
                    aria-label={`Download ${shot.label ?? `key visual ${position}`}`}
                    data-tip="Download"
                  >
                    {iconDownload}
                  </button>
                </div>
                <figcaption className="design-gallery-caption">
                  {shot.label ?? "Key visual"}
                </figcaption>
              </figure>
            ))}
        </div>
      )}
    </section>
  );
}

function FieldEditor({
  field,
  value,
  onCommit,
}: {
  field: ArtDirectionTextField;
  value: string;
  onCommit: (next: string) => void;
}) {
  const copy = FIELD_COPY[field];
  return (
    <section className="design-section">
      <label className="design-label" htmlFor={`design-${field}`}>
        {copy.label}
      </label>
      <p className="design-hint">{copy.hint}</p>
      <CommitTextarea
        id={`design-${field}`}
        rows={3}
        value={value}
        placeholder={copy.placeholder}
        onCommit={onCommit}
      />
    </section>
  );
}

/**
 * A textarea that edits locally and writes to the doc on blur (see the module
 * note). It re-syncs from `value` whenever the doc changes underneath — so a
 * teammate's edit lands live — EXCEPT while this user is focused in it, which
 * would otherwise yank the caret mid-sentence.
 */
function CommitTextarea({
  id,
  rows,
  value,
  placeholder,
  onCommit,
}: {
  id: string;
  rows: number;
  value: string;
  placeholder: string;
  onCommit: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setDraft(value);
  }, [value, focused]);

  return (
    <textarea
      id={id}
      className="design-input"
      rows={rows}
      value={draft}
      placeholder={placeholder}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        if (draft !== value) onCommit(draft);
      }}
    />
  );
}

/**
 * Palette editing. Every change writes the WHOLE set (the stored key is one
 * whole object — a palette is chosen as a set, not swatch by swatch), so this
 * is the one place in the panel where a per-keystroke write would be wrong for
 * a second reason: it would also fight the other swatches' concurrent edits.
 * Colour inputs commit on change (they are pickers, not typing); the role name
 * commits on blur like every other text field.
 */
function PaletteEditor({
  swatches,
  onChange,
}: {
  swatches: readonly PaletteSwatch[];
  onChange: (next: PaletteSwatch[]) => void;
}) {
  const replace = (index: number, patch: Partial<PaletteSwatch>) => {
    onChange(swatches.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  };

  return (
    <div className="design-palette">
      {swatches.length === 0 && (
        <p className="design-hint">No palette yet — generate one, or add a colour.</p>
      )}
      {swatches.map((swatch, index) => (
        <div className="design-swatch-row" key={`${swatch.hex}-${index}`}>
          <input
            type="color"
            className="design-swatch"
            value={normalizeForPicker(swatch.hex)}
            onChange={(e) => replace(index, { hex: e.target.value })}
            aria-label={`Colour ${index + 1}`}
          />
          <CommitTextarea
            id={`design-swatch-${index}`}
            rows={1}
            value={swatch.name}
            placeholder="role — e.g. background"
            onCommit={(name) => replace(index, { name })}
          />
          <button
            type="button"
            className="icon-btn"
            onClick={() => onChange(swatches.filter((_, i) => i !== index))}
            aria-label={`Remove colour ${index + 1}`}
            data-tip="Remove"
          >
            ✕
          </button>
        </div>
      ))}
      {swatches.length < 8 && (
        <button
          type="button"
          className="btn design-add-swatch"
          onClick={() => onChange([...swatches, { hex: "#888888", name: "" }])}
        >
          + Add colour
        </button>
      )}
    </div>
  );
}

/** `<input type="color">` accepts only `#rrggbb` — expand the 3-digit form. */
function normalizeForPicker(hex: string): string {
  if (/^#[0-9a-f]{3}$/i.test(hex)) {
    const [r, g, b] = [hex[1], hex[2], hex[3]];
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return /^#[0-9a-f]{6}$/i.test(hex) ? hex : "#888888";
}

function formatWhen(at: number): string {
  const seconds = Math.round((Date.now() - at) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}
